import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Box, Paper, Typography, Chip, Alert, Skeleton, Button, Divider, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  CircularProgress,
} from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { configApi } from '../../../services/configApi';
import { formatDate, toPercent, formatMoney } from '../../../utils/configUtils';
import ConfigDrawer from './ConfigDrawer';
import { useConfirm } from '../../../common/confirmDialog/useConfirm';
import StatusChip from '../../../common/statusChip/StatusChip';

const formatMethod = (v) => v ? v.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : v;

const hdSx = {
  fontFamily: 'Heebo', fontWeight: 600, color: '#362B24', fontSize: '13px',
  bgcolor: '#f9fafb', borderBottom: '1px solid #EAEAEA', py: 1.5, px: 2, textAlign: 'center',
};
const cellSx = { fontFamily: 'Heebo', fontSize: '13px', color: '#362B24', px: 2, py: 1.5, textAlign: 'center' };

const secLabel = { fontFamily: 'Heebo', fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: 0.8, color: 'rgba(0,0,0,0.4)', mb: 1 };
const valRow = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px 24px' };
function KV({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontFamily: 'Heebo', fontSize: '10px', color: 'rgba(0,0,0,0.4)', mb: 0.2 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Heebo', fontSize: '13px', fontWeight: 500, color: '#362B24' }}>{value ?? 'N/A'}</Typography>
    </Box>
  );
}

function ActiveBanner({ active, loading, error }) {
  const [open, setOpen] = useState(false);

  if (loading) {
    return <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '12px', mb: 2 }} />;
  }
  if (error) {
    return <Alert severity="error" sx={{ mb: 2, fontFamily: 'Heebo' }}>Failed to load active config.</Alert>;
  }
  if (!active) {
    return (
      <Box sx={{ px: 2.5, py: 1.25, mb: 2, borderRadius: '12px', bgcolor: '#fafafa', border: '1px solid #eaeaea' }}>
        <Typography sx={{ fontFamily: 'Heebo', fontSize: '13px', color: 'rgba(0,0,0,0.45)' }}>
          No active configuration. Create one and activate it.
        </Typography>
      </Box>
    );
  }

  const fee = active?.app_fee ?? {};
  const cur = active.currency || 'USD';
  const stats = [
    active.currency,
    formatDate(active.effective_from) + (active.effective_to ? ` to ${formatDate(active.effective_to)}` : ' onwards'),
    fee.is_active ? `${toPercent(fee.percentage)} of ${formatMethod(fee.apply_to)}` : null,
    `${(active.tax ?? []).length} tax`,
    `${(active.shipping ?? []).length} shipping`,
  ].filter(Boolean);

  return (
    <Box sx={{ mb: 2, borderRadius: '12px', bgcolor: '#fff', border: '1px solid #e2e8f0', borderLeft: '3px solid #065F46', overflow: 'hidden' }}>
      {/* collapsed row */}
      <Box
        onClick={() => setOpen((o) => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: '#fafafa' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Chip label={active.version_code} color="success" size="small" sx={{ height: 20, fontSize: '10px', fontFamily: 'Heebo', fontWeight: 600 }} />
          <Typography sx={{ fontFamily: 'Heebo', fontWeight: 700, fontSize: '14px', color: '#362B24' }}>{active.name}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, flex: 1 }}>
          {stats.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Typography sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.25)', mx: 0.25 }}>·</Typography>}
              <Typography sx={{ fontFamily: 'Heebo', fontSize: '12px', color: '#362B24' }}>{s}</Typography>
            </React.Fragment>
          ))}
        </Box>
        <KeyboardArrowDownOutlinedIcon sx={{ fontSize: 24, color: 'rgba(0,0,0,0.35)', flexShrink: 0, transition: 'transform 0.22s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </Box>

      {/* expanded detail */}
      <Collapse in={open}>
        <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5, borderTop: '1px solid #f0f0f0', pt: 2 }}>

          {/* Basic */}
          <Box>
            <Typography sx={secLabel}>General</Typography>
            <Box sx={valRow}>
              <KV label="Version Code" value={active.version_code} />
              <KV label="Name" value={active.name} />
              <KV label="Currency" value={active.currency} />
              <KV label="Effective From" value={formatDate(active.effective_from)} />
              <KV label="Effective To" value={active.effective_to ? formatDate(active.effective_to) : 'Open'} />
              <KV label="Created" value={formatDate(active.createdAt)} />
              <KV label="Updated" value={formatDate(active.updatedAt)} />
            </Box>
          </Box>

          {/* App Fee */}
          {fee && (
            <Box>
              <Typography sx={secLabel}>App Fee</Typography>
              <Box sx={valRow}>
                <KV label="Commission" value={toPercent(fee.percentage)} />
                <KV label="Flat Fee" value={formatMoney(fee.flat, cur)} />
                <KV label="Apply To" value={formatMethod(fee.apply_to)} />
                <KV label="Min Fee" value={formatMoney(fee.min_fee, cur)} />
                <KV label="Max Fee" value={fee.max_fee ? formatMoney(fee.max_fee, cur) : 'No cap'} />
                <KV label="Active" value={fee.is_active ? 'Yes' : 'No'} />
              </Box>
            </Box>
          )}

          {/* Tax */}
          {(active.tax ?? []).length > 0 && (
            <Box>
              <Typography sx={secLabel}>Tax ({active.tax.length})</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {active.tax.map((t, i) => (
                  <Box key={i} sx={{ ...valRow, p: 1.5, bgcolor: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                    <KV label="Type" value={t.tax_type} />
                    <KV label="Rate" value={toPercent(t.rate)} />
                    <KV label="Inclusive" value={t.is_inclusive ? 'Yes' : 'No'} />
                    <KV label="Priority" value={String(t.priority ?? 0)} />
                    <KV label="Active" value={t.is_active ? 'Yes' : 'No'} />
                    {t.region && <KV label="Region" value={t.region} />}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Shipping */}
          {(active.shipping ?? []).length > 0 && (
            <Box>
              <Typography sx={secLabel}>Shipping ({active.shipping.length})</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {active.shipping.map((s, i) => (
                  <Box key={i} sx={{ ...valRow, p: 1.5, bgcolor: '#fafafa', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                    <KV label="Method" value={formatMethod(s.method)} />
                    <KV label="Base Cost" value={formatMoney(s.base_cost, cur)} />
                    <KV label="Free Above" value={s.free_above ? formatMoney(s.free_above, cur) : 'Never free'} />
                    <KV label="Active" value={s.is_active ? 'Yes' : 'No'} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

        </Box>
      </Collapse>
    </Box>
  );
}

export default function PlatformConfigTab({ openTrigger }) {
  const token = useSelector((state) => state?.user?.user?.token);
  const confirm = useConfirm();

  const [configs, setConfigs] = useState([]);
  const [activeConfig, setActiveConfig] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [activeError, setActiveError] = useState(false);
  const [activatingId, setActivatingId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [selectedConfig, setSelectedConfig] = useState(null);

  useEffect(() => {
    if (openTrigger > 0) {
      setSelectedConfig(null);
      setDrawerMode('create');
      setDrawerOpen(true);
    }
  }, [openTrigger]);

  const refetch = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setActiveError(false);
    try {
      const [listRes, activeRes] = await Promise.all([
        configApi.listConfigs(token),
        configApi.getActiveConfig(token),
      ]);
      if (!cancelled) {
        setConfigs(listRes?.data ?? []);
        setActiveConfig(activeRes?.data ?? null);
      }
    } catch (e) {
      if (!cancelled) {
        setActiveError(true);
        toast.error(e?.error || 'Failed to load configurations.');
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const cancel = refetch();
    return () => { if (typeof cancel === 'function') cancel(); };
  }, [refetch]);

  const handleActivate = async (id) => {
    const ok = await confirm({
      title: 'Activate Configuration',
      description: 'This will replace the current active config and immediately affect all users on the platform. Are you sure you want to continue?',
      confirmText: 'Activate',
      cancelText: 'Cancel',
    });
    if (!ok) return;
    setActivatingId(id);
    try {
      await configApi.activateConfig(token, id);
      toast.success('Configuration activated.');
      refetch();
    } catch (e) {
      toast.error(e?.error || 'Failed to activate configuration.');
    } finally {
      setActivatingId(null);
    }
  };

  const openViewDrawer = (cfg) => {
    setSelectedConfig(cfg);
    setDrawerMode('view');
    setDrawerOpen(true);
  };

  const handleDrawerSuccess = () => {
    setDrawerOpen(false);
    refetch();
  };

  const renderSkeletonRows = () =>
    [1, 2, 3].map((i) => (
      <TableRow key={i}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
          <TableCell key={j} sx={cellSx}><Skeleton /></TableCell>
        ))}
      </TableRow>
    ));

  return (
    <Box>
      <ActiveBanner
        active={activeConfig}
        loading={loading && activeConfig === undefined}
        error={activeError}
      />

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
        <Table>
          <TableHead>
            <TableRow>
              {['Version', 'Name', 'Currency', 'Status', 'Effective From', 'Effective To', 'Updated By', 'Actions'].map((h) => (
                <TableCell key={h} sx={hdSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? renderSkeletonRows() : configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ ...cellSx, textAlign: 'center', py: 4, opacity: 0.5 }}>
                  No configurations found.
                </TableCell>
              </TableRow>
            ) : configs.map((cfg) => (
              <TableRow key={cfg._id} hover>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{cfg.version_code}</TableCell>
                <TableCell sx={cellSx}>{cfg.name}</TableCell>
                <TableCell sx={cellSx}>{cfg.currency}</TableCell>
                <TableCell sx={cellSx}>
                  <StatusChip value={cfg.is_active ? 'active' : 'inactive'} />
                </TableCell>
                <TableCell sx={cellSx}>{formatDate(cfg.effective_from)}</TableCell>
                <TableCell sx={cellSx}>{formatDate(cfg.effective_to)}</TableCell>
                <TableCell sx={cellSx}>{cfg.updated_by?.name || 'N/A'}</TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={() => openViewDrawer(cfg)}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                  {!cfg.is_active && (
                    <Button
                      size="small"
                      disabled={activatingId === cfg._id}
                      startIcon={activatingId === cfg._id ? <CircularProgress size={12} color="inherit" /> : null}
                      onClick={() => handleActivate(cfg._id)}
                      sx={{
                        ml: 0.5, fontFamily: 'Heebo', textTransform: 'none', fontSize: '12px',
                        color: '#065F46', borderColor: '#065F46',
                        '&:hover': { bgcolor: 'rgba(6,95,70,0.06)', borderColor: '#065F46' },
                      }}
                      variant="outlined"
                    >
                      {activatingId === cfg._id ? 'Activating…' : 'Activate'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfigDrawer
        open={drawerOpen}
        mode={drawerMode}
        config={selectedConfig}
        onClose={() => setDrawerOpen(false)}
        onSuccess={handleDrawerSuccess}
        onActivate={handleActivate}
        activating={activatingId !== null}
      />
    </Box>
  );
}
